// Плагин нумерации: при каждом изменении документа (и метаданных/источников) пересчитывает номера
// (core/numbering.ts), публикует их в numberingStore и ставит декорации:
//   заголовки     - data-num="1.2"                   (CSS: .sto-h::before { content: attr(data-num) })
//   подписи       - data-label="Рисунок 3. "          (CSS: .sto-caption::before)
//   приложения    - data-label="Приложение А", data-status="(обязательное)" на appendixTitle
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { useSyncExternalStore } from 'react';
import { computeNumbering, emptyNumbering, pmAccess, type Numbering, type NumInfo } from '../numbering';
import { appStore } from '../store';
import { NBSP } from '../schema/constants';

// ------------------------------------------------------------------------------------------------ хранилище номеров
let current: Numbering = emptyNumbering();
const listeners = new Set<() => void>();
export const numberingStore = {
  get: () => current,
  set(n: Numbering) { current = n; listeners.forEach((f) => f()); },
  subscribe(fn: () => void) { listeners.add(fn); return () => { listeners.delete(fn); }; },
};
/** Все номера (перерисовка при любом изменении нумерации). */
export function useNumbering(): Numbering {
  return useSyncExternalStore(numberingStore.subscribe, numberingStore.get);
}
/** Номер одного объекта по id (undefined - не нумеруется / нет такого). */
export function useNum(id: string | null | undefined): NumInfo | undefined {
  const n = useNumbering();
  return id ? n.byId.get(id) : undefined;
}

export const numberingKey = new PluginKey<{ nb: Numbering; deco: DecorationSet }>('stoNumbering');

function compute(doc: PMNode): Numbering {
  const s = appStore.getState();
  return computeNumbering(doc, pmAccess, s.meta, s.sources);
}

const CAP_WORD: Record<string, string> = { figure: 'Рисунок', stoTable: 'Таблица', listing: 'Листинг' };

function decorations(doc: PMNode, nb: Numbering): DecorationSet {
  const decos: Decoration[] = [];
  const visit = (node: PMNode, pos: number) => {
    const t = node.type.name;
    const id = node.attrs?.id as string | undefined;
    if (t === 'heading') {
      const info = id ? nb.byId.get(id) : undefined;
      decos.push(Decoration.node(pos, pos + node.nodeSize, { 'data-num': info?.num ? info.num + NBSP : '' }));
    } else if (CAP_WORD[t]) {
      const info = id ? nb.byId.get(id) : undefined;
      node.forEach((child, off) => {
        if (child.type.name === 'caption') {
          const p = pos + 1 + off;
          const label = `${CAP_WORD[t]}${NBSP}${info?.num ?? '?'}` + (child.content.size ? '. ' : '');
          decos.push(Decoration.node(p, p + child.nodeSize, { 'data-label': label }));
        }
      });
    } else if (t === 'appendix') {
      const info = id ? nb.byId.get(id) : undefined;
      node.forEach((child, off) => {
        if (child.type.name === 'appendixTitle') {
          const p = pos + 1 + off;
          decos.push(Decoration.node(p, p + child.nodeSize, {
            'data-label': `Приложение${NBSP}${info?.letter ?? '?'}`, 'data-status': `(${node.attrs.status || 'обязательное'})`,
          }));
        }
      });
    }
    if (t === 'appendix') node.forEach((c, off) => visit(c, pos + 1 + off));
  };
  doc.forEach((c, off) => visit(c, off));
  return DecorationSet.create(doc, decos);
}

export const NumberingExtension = Extension.create({
  name: 'stoNumbering',
  addProseMirrorPlugins() {
    return [new Plugin({
      key: numberingKey,
      state: {
        init: (_, state) => {
          const nb = compute(state.doc);
          return { nb, deco: decorations(state.doc, nb) };
        },
        apply: (tr, prev, _old, state) => {
          if (!tr.docChanged && !tr.getMeta(numberingKey)) return prev;
          const nb = compute(state.doc);
          return { nb, deco: decorations(state.doc, nb) };
        },
      },
      props: { decorations: (state) => numberingKey.getState(state)?.deco },
      view: (view) => {
        numberingStore.set(numberingKey.getState(view.state)!.nb);
        return {
          update: (v, prevState) => {
            const a = numberingKey.getState(v.state);
            const b = numberingKey.getState(prevState);
            if (a && a !== b) numberingStore.set(a.nb);
          },
        };
      },
    })];
  },
});

/** Пересчитать номера после изменения метаданных или источников (не документа). */
export function refreshNumbering() {
  const ed = appStore.getState().editor;
  if (ed && !ed.isDestroyed) ed.view.dispatch(ed.state.tr.setMeta(numberingKey, true).setMeta('addToHistory', false));
}
